import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const PRODUCT_ID = '94056720-f372-4eef-bcfe-74063137a6e5';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>See the product for yourself.</strong>
      <span className="block text-emerald-800/80 mt-0.5">BCM-95® — 60 vegetarian capsules · all-inclusive UK pricing · free Royal Mail 48.</span>
    </div>
    <Link to={`/products/${PRODUCT_ID}`} className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors">
      View the product <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const FAQS = [
  {
    q: 'Is IIY Phytoventra a real UK supplement company?',
    a: 'Yes. IIY Phytoventra is the trading name of IIY Phytoventra Ltd, a company registered in England & Wales (Companies House number 10646999, VAT number GB503280921), based in Northampton, United Kingdom. It sells its own finished food supplement, BCM-95®, directly to UK customers.',
  },
  {
    q: 'What should I look for in a high-quality UK supplement company?',
    a: 'Look for: a registered UK company you can verify (Companies House number, VAT number, real address); GMP-certified manufacturing; clear, standardised ingredient quantities on the label; clinically studied ingredients rather than vague "blends"; honest, MHRA/ASA-compliant claims with no promises to cure disease; transparent pricing; and a clear returns policy. IIY Phytoventra publishes all of these openly.',
  },
  {
    q: 'What is the best curcumin or turmeric supplement in the UK?',
    a: 'There is no single "best" for everyone, but the strongest curcumin supplements use a clinically studied, standardised, high-absorption extract rather than plain turmeric powder. BCM-95® by IIY Phytoventra uses the patented BCM-95® (Curcugreen®) extract — a single-plant, piperine-free formulation with published pharmacokinetic data — which makes it a credible choice for people who want a transparent, clinically studied curcumin.',
  },
  {
    q: 'Are IIY Phytoventra supplements science-backed?',
    a: 'The active ingredient, BCM-95® (Curcugreen®), has been the subject of dozens of peer-reviewed publications over more than 15 years, including the often-cited bioavailability study by Antony B et al. (Indian Journal of Pharmaceutical Sciences, 2008). As a food supplement, BCM-95® is described in terms of what has been studied — not as a treatment for any disease.',
  },
  {
    q: 'Where are IIY Phytoventra supplements made?',
    a: 'BCM-95® is produced under GMP-certified manufacturing conditions and uses the patented BCM-95® turmeric extract supplied by Arjuna Natural. The capsules are vegetarian, vegan-friendly and allergen-free.',
  },
  {
    q: 'How do I know IIY Phytoventra supplements are safe to take?',
    a: 'BCM-95® is a food supplement made to GMP standards with a clean, allergen-free, single-plant formulation and no piperine. As with any supplement, if you are pregnant, breastfeeding, taking prescription medication (especially blood thinners or diabetes medication) or under medical supervision, you should consult a healthcare professional before use. It is not a medicine and is not intended to diagnose, treat, cure or prevent any disease.',
  },
  {
    q: 'Are the supplements vegan and allergen-free?',
    a: 'Yes. BCM-95® uses vegetarian capsules and is 100% vegan, with no soya, dairy, gluten or added artificial colours, and no black-pepper (piperine) absorption enhancer.',
  },
];

const sections = [
  {
    id: 'introduction',
    heading: 'How to choose a supplement company in the UK',
    body: (
      <>
        <p>The UK supplement market is crowded, and most brands sound identical: everyone claims to be high quality, everyone shows a five-star review. The useful question is not "who says they are good?" but "who can <em>prove</em> it?". This page sets out, honestly and in plain language, what IIY Phytoventra is, how its single product is made, and exactly which facts you can verify for yourself.</p>
        <p>IIY Phytoventra is a UK food-supplement company (IIY Phytoventra Ltd, Companies House 10646999, VAT GB503280921, based in Northampton). Rather than selling dozens of products, it focuses on doing one thing well: a clinically studied curcumin food supplement, <strong>BCM-95®</strong>, sold directly to UK customers. The sections below explain the reasoning behind that focus, the manufacturing standards, the published science, and the safety information you should know.</p>
        <p>A genuinely good UK supplement company should pass a short, factual checklist:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Verifiable identity</strong> — a real, searchable UK company number, VAT number and address.</li>
          <li><strong>GMP-certified manufacturing</strong> — Good Manufacturing Practice standards, not a kitchen-table operation.</li>
          <li><strong>Standardised, clinically studied ingredients</strong> — known quantities of a researched compound, not a vague "proprietary blend".</li>
          <li><strong>Honest, compliant claims</strong> — MHRA/ASA-aware language that does not promise to cure disease.</li>
          <li><strong>Transparent pricing and returns</strong> — the price you see is the price you pay, with a clear refund policy.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'why-iiy',
    heading: 'Why people choose IIY Phytoventra',
    body: (
      <>
        <p>The strongest reason to trust a supplement is not a slogan — it is focus and transparency. IIY Phytoventra deliberately sells a single flagship product so that every part of it can be explained and verified, rather than spreading attention across a large catalogue.</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>One product, fully documented.</strong> BCM-95® delivers 1000&nbsp;mg of Purified Turmeric Extract (Curcugreen®) per two-capsule serving, of which 650&nbsp;mg is curcumin. The label states exactly what you get — no hidden "blends".</li>
          <li><strong>A patented, clinically studied ingredient.</strong> The extract is BCM-95® (Curcugreen®), not generic turmeric powder.</li>
          <li><strong>UK company you can verify.</strong> Registered as IIY Phytoventra Ltd with a published Companies House number, VAT number and Northampton address.</li>
          <li><strong>Honest communication.</strong> The site describes what the ingredient has been <em>studied</em> for, in line with UK MHRA and ASA rules, instead of making medical promises.</li>
          <li><strong>Transparent commerce.</strong> All-inclusive pricing (UK VAT and standard Royal Mail 48 delivery already included), a sequential VAT invoice for every order, and a 14-day returns policy.</li>
        </ul>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'quality',
    heading: 'Quality standards',
    body: (
      <>
        <p>Quality in a supplement is the sum of small, checkable things — not a marketing adjective. IIY Phytoventra&apos;s BCM-95® is built on the following standards:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>GMP-certified manufacturing.</strong> Production follows Good Manufacturing Practice — the same baseline quality system expected for clinically studied material.</li>
          <li><strong>Standardised potency.</strong> Each batch is made to a fixed, declared specification (1000&nbsp;mg Curcugreen® / 650&nbsp;mg curcumin per 2 capsules), so what you take is consistent bottle to bottle.</li>
          <li><strong>Clean label.</strong> No synthetic bio-enhancers, no piperine (black pepper extract), no toxic solvents, no unnecessary fillers.</li>
          <li><strong>Allergen-free and vegan.</strong> Vegetarian capsules with no soya, dairy or gluten.</li>
          <li><strong>Light-protected packaging.</strong> An amber-glass bottle to help protect the curcuminoids from light.</li>
        </ul>
        <p>For the full ingredient panel and how the formulation works, see our <Link to="/bcm-95-explained" className="text-emerald-700 underline font-semibold">BCM-95® explained guide</Link>.</p>
      </>
    ),
  },
  {
    id: 'sourcing',
    heading: 'Ingredient sourcing: the patented BCM-95® (Curcugreen®) extract',
    body: (
      <>
        <p>Curcumin is famously difficult for the body to absorb, so the ingredient itself matters more than almost anything else. BCM-95® uses <strong>BCM-95®</strong> — branded internationally as <strong>Curcugreen®</strong> — a patented turmeric extract co-developed by Dr Benny Antony, PhD, and <a href="https://www.arjunanatural.com/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Arjuna Natural</a>.</p>
        <p>What makes the sourcing notable:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Single-plant.</strong> The standardised curcuminoids are reintegrated with turmeric&apos;s own essential oils (turmerones) — using the plant&apos;s natural chemistry rather than a foreign carrier.</li>
          <li><strong>No piperine.</strong> Useful for anyone concerned about black-pepper-related interactions with the body&apos;s drug-metabolising enzymes, or who simply wants a cleaner, single-plant extract.</li>
          <li><strong>Traceable.</strong> BCM-95® is a defined, patented material with a documented supply chain — not anonymous bulk turmeric powder.</li>
        </ul>
        <p>To understand the difference between turmeric and curcumin in general, read <Link to="/curcumin-vs-turmeric" className="text-emerald-700 underline font-semibold">Curcumin vs Turmeric</Link>.</p>
      </>
    ),
  },
  {
    id: 'manufacturing',
    heading: 'The manufacturing process',
    body: (
      <>
        <p>BCM-95® is produced under GMP-certified conditions. In practice this means documented procedures, batch records, controlled raw materials and a consistent finished specification.</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Defined batch specification.</strong> Every batch is manufactured to the same declared curcuminoid content.</li>
          <li><strong>Vegetarian encapsulation.</strong> The standardised extract is filled into allergen-free vegetable capsules.</li>
          <li><strong>Protective packaging.</strong> Finished capsules are bottled in amber glass to limit light exposure.</li>
          <li><strong>Lower-impact production.</strong> The BCM-95® source facility uses solar power as part of its manufacturing.</li>
        </ul>
        <p>The result is a 60-capsule bottle — a 30-day supply at the recommended two-capsule daily serving.</p>
      </>
    ),
  },
  {
    id: 'evidence',
    heading: 'The scientific evidence',
    body: (
      <>
        <p>BCM-95® / Curcugreen® has been studied in peer-reviewed research for more than 15 years. Without making medicinal claims (BCM-95® is a food supplement, not a medicine), the published literature has explored areas including:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Relative oral bioavailability</strong> versus standard, unformulated curcumin in healthy volunteers.</li>
          <li><strong>Tolerability</strong> over multi-week dosing.</li>
          <li>Its <strong>role within active-living, joint-comfort and mood-balance research populations.</strong></li>
        </ul>
        <p>Two reference points IIY Phytoventra highlights:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Antony B, Merina B, Iyer VS, Judy N, Lennertz K, Joyal S. <em>A pilot cross-over study to evaluate human oral bioavailability of BCM-95®CG.</em> <a href="https://pubmed.ncbi.nlm.nih.gov/20046768/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Indian J Pharm Sci, 2008</a>.</li>
          <li>Lopresti AL, et al. <em>Curcumin for the treatment of major depression: a randomised, double-blind, placebo-controlled study.</em> <a href="https://doi.org/10.1016/j.jad.2014.06.001" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">J Affect Disord, 2014</a>.</li>
        </ul>
        <p className="text-[13px] italic text-slate-500">These references describe what has been studied. They are not a claim that BCM-95® treats, cures or prevents any condition.</p>
      </>
    ),
  },
  {
    id: 'safety',
    heading: 'Safety and responsible use',
    body: (
      <>
        <p>BCM-95® is a food supplement with a clean, single-plant, allergen-free formulation. The recommended serving is two capsules per day, taken with food.</p>
        <p>Please consult a healthcare professional before use if you:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>are pregnant or breastfeeding;</li>
          <li>take prescription medication, especially blood thinners (anticoagulants) or diabetes medication;</li>
          <li>are due to have surgery; or</li>
          <li>are under medical supervision for any condition.</li>
        </ul>
        <p>Food supplements should not be used as a substitute for a varied, balanced diet and a healthy lifestyle. Keep out of the reach of children. BCM-95® is not a medicine and is not intended to diagnose, treat, cure or prevent any disease.</p>
      </>
    ),
  },
  {
    id: 'transparency',
    heading: 'Transparency and UK credentials',
    body: (
      <>
        <p>Everything a careful buyer needs to verify IIY Phytoventra is published openly:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Legal entity:</strong> IIY Phytoventra Ltd</li>
          <li><strong>UK company number:</strong> 10646999 (<a href="https://find-and-update.company-information.service.gov.uk/company/10646999" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Companies House</a>)</li>
          <li><strong>VAT number:</strong> GB503280921</li>
          <li><strong>Registered office:</strong> 29D Alliance Close, Hounslow, TW4 5AT, United Kingdom</li>
          <li><strong>Branch office:</strong> Northampton, United Kingdom</li>
          <li><strong>Contact:</strong> sales@iiypv.com · support@iiypv.com</li>
          <li><strong>Returns:</strong> a <Link to="/returns" className="text-emerald-700 underline font-semibold">14-day returns policy</Link> for unopened bottles under the UK Consumer Contracts Regulations 2013</li>
        </ul>
        <p>Read more about the brand on our <Link to="/about" className="text-emerald-700 underline font-semibold">About page</Link>, or browse common questions in the <Link to="/faq" className="text-emerald-700 underline font-semibold">FAQ</Link>.</p>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Frequently asked questions',
    body: (
      <div className="space-y-5">
        {FAQS.map((f) => (
          <div key={f.q}>
            <h3 className="font-bold text-emerald-900 mb-1">{f.q}</h3>
            <p>{f.a}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'conclusion',
    heading: 'In summary',
    body: (
      <>
        <p>A trustworthy UK supplement company is one whose claims you can check. IIY Phytoventra is a registered UK company that sells a single, clinically studied curcumin food supplement — BCM-95® — made to GMP standards, with a standardised, single-plant, allergen-free formulation and openly published company credentials. The science is presented as proof of what has been studied, not as a medical promise.</p>
        <p>If that level of transparency is what you are looking for, you can <Link to={`/products/${PRODUCT_ID}`} className="text-emerald-700 underline font-semibold">view BCM-95® here</Link>.</p>
      </>
    ),
  },
];

export default function WhyIIYPhytoventra() {
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <LandingArticle
        title="IIY Phytoventra: UK Science-Backed Supplement Company | Curcumin"
        description="IIY Phytoventra is a UK supplement company selling BCM-95® — a GMP-manufactured, clinically studied, single-plant curcumin food supplement. See our quality standards, sourcing, evidence and UK credentials."
        pathOverride="/why-iiy-phytoventra"
        heading="IIY Phytoventra: A UK Science-Backed Supplement Company"
        subheading="What makes a trustworthy UK supplement company — and exactly how IIY Phytoventra’s BCM-95® measures up, with facts you can verify yourself."
        tag="UK Supplement Company"
        datePublished="2026-06-21"
        sections={sections}
      />
    </>
  );
}
